import { parseIntakeAttribution } from './intakeAttribution';

const MEMORY_KEY = 'curegate-attribution';

const MEMORY_FIELDS = [
  'acquisition_source',
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
  'fbclid',
  'ad_campaign_id',
  'cancer_type',
];

function readParam(search, key) {
  try {
    const params = new URLSearchParams(typeof search === 'string' ? search : '');
    const value = params.get(key);
    return value && String(value).trim() ? String(value).trim() : null;
  } catch {
    return null;
  }
}

export function readAttributionMemory() {
  if (typeof window === 'undefined') return {};
  try {
    const raw = sessionStorage.getItem(MEMORY_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

export function rememberAttribution(search) {
  if (typeof window === 'undefined') return readAttributionMemory();
  const parsed = parseIntakeAttribution(search || '');
  const utmTerm = readParam(search, 'utm_term');
  const existing = readAttributionMemory();
  const next = { ...existing };
  MEMORY_FIELDS.forEach((key) => {
    if (key === 'utm_term') {
      if (utmTerm) next.utm_term = utmTerm;
      return;
    }
    if (parsed?.[key]) next[key] = parsed[key];
  });
  const hasValue = MEMORY_FIELDS.some((key) => next[key]);
  if (hasValue) {
    try {
      sessionStorage.setItem(MEMORY_KEY, JSON.stringify(next));
    } catch {
      /* private mode can block storage; the current URL still carries UTMs */
    }
  }
  return next;
}

// Cancer type is FLOW-scoped, not first-touch: it reflects the page/flow the patient is
// in now (a condition landing, or a ?condition= param), never the original ad campaign.
// UTMs/fbclid stay first-touch.
export const GENERIC_ENTRY_PATHS = new Set(['/', '/conditions', '/patients', '/hopefirst', '/clinical-trials', '/start/cancer']);

export function setFlowCancerType(cancerType) {
  if (typeof window === 'undefined') return;
  try {
    const next = { ...readAttributionMemory() };
    if (cancerType) next.cancer_type = cancerType; else delete next.cancer_type;
    sessionStorage.setItem(MEMORY_KEY, JSON.stringify(next));
  } catch { /* ignore */ }
}

// Called on every route change after rememberAttribution(). Generic pages reset the
// flow cancer type unless the URL itself names one.
export function syncFlowCancerType(pathname, search) {
  if (!GENERIC_ENTRY_PATHS.has(pathname)) return;
  if (readParam(search, 'condition') || readParam(search, 'cancer_type')) return;
  setFlowCancerType(null);
}

export function attributionFromLocation(search) {
  const parsed = parseIntakeAttribution(search || '');
  const memory = readAttributionMemory();
  const utmTerm = readParam(search, 'utm_term') || memory.utm_term || null;
  return {
    ...parsed,
    cancer_type: parsed.cancer_type || memory.cancer_type || null,
    acquisition_source: parsed.acquisition_source || memory.acquisition_source || null,
    utm_source: parsed.utm_source || memory.utm_source || null,
    utm_medium: parsed.utm_medium || memory.utm_medium || null,
    utm_campaign: parsed.utm_campaign || memory.utm_campaign || null,
    utm_content: parsed.utm_content || memory.utm_content || null,
    utm_term: utmTerm,
    fbclid: parsed.fbclid || memory.fbclid || null,
    ad_campaign_id: parsed.ad_campaign_id || memory.ad_campaign_id || null,
  };
}

export function appendStoredAttribution(path) {
  if (typeof window === 'undefined' || !path) return path;
  const memory = readAttributionMemory();
  let url;
  try {
    url = new URL(path, 'https://curegate.ai');
  } catch {
    return path;
  }
  const pairs = {
    utm_source: memory.utm_source,
    utm_medium: memory.utm_medium,
    utm_campaign: memory.utm_campaign,
    utm_content: memory.utm_content,
    utm_term: memory.utm_term,
    fbclid: memory.fbclid,
  };
  Object.entries(pairs).forEach(([key, value]) => {
    if (value && !url.searchParams.get(key)) url.searchParams.set(key, value);
  });
  return `${url.pathname}${url.search}${url.hash}`;
}
